import * as React from "react";
import { connect } from "react-redux";
import $ from "jquery";
import { Toaster } from "../../page-framework/toaster";
import { User } from "../../../utilities/user";
import { Person } from "@radivision/graphql/lib/ts/graphql/person";
import { RdvButton } from "../../page-framework/rdv-button";
import { AppState, store } from "../../../redux";
import { ERROR_MESSAGES } from "../../../constants/errors-constants";
import { setToastMessage } from "../../../redux/actions";
import { TOAST_ALERT } from "../../../redux/toast/constants";
import isEmpty from "lodash.isempty";

interface ComponentProps {}

interface MapDispatchProps {}

interface MapStateProps {
  user: Person;
}

interface PasswordForm {
  currentPassword: string;
  isValidPassword: boolean;
  isMatched: boolean;
  newPassword: string;
  matchingPassword: string;
  displayError: boolean;
}

interface IErrors {
  hasError: boolean;
  errors: string[];
}

interface ComponentState {
  form: PasswordForm;
  loading: boolean;
  errors: IErrors;
}
export type Props = ComponentProps & MapDispatchProps & MapStateProps;

const EmptyForm: PasswordForm = {
  currentPassword: "",
  matchingPassword: "",
  isValidPassword: true,
  isMatched: true,
  displayError: false,
  newPassword: "",
};

class ChangePasswordComponent extends React.Component<Props, ComponentState> {
  constructor(props: Props) {
    super(props);

    const errors: IErrors = { hasError: false, errors: [] };
    const loading = false;
    this.state = { form: EmptyForm, errors, loading };

    this.updateForm = this.updateForm.bind(this);
    this.onChangePassword = this.onChangePassword.bind(this);
  }

  updateForm(field: string, value: string) {
    const form = { ...this.state.form };
    const isValidPassword = User.validatePassword(value);
    const isValidNewPassword = field === "newPassword" ? { isValidPassword } : {};
    this.setState({ form: { ...form, [field]: value, ...isValidNewPassword } });
    return;
  }

  onChangePassword(): void {
    if (this.state.loading) return;

    const isMatched = this.state.form.matchingPassword === this.state.form.newPassword;
    const hasCurrentPassword = !isEmpty(this.state.form.currentPassword);
    const hasNewPassword = !isEmpty(this.state.form.newPassword);
    const isValidPassword = User.validatePassword(this.state.form.newPassword);

    if (!hasCurrentPassword) {
      store.dispatch(setToastMessage({ msg: "Please enter the current password", type: TOAST_ALERT }));
      return;
    }
    if (!hasNewPassword) {
      store.dispatch(setToastMessage({ msg: "Please enter the new password", type: TOAST_ALERT }));
      return;
    }
    if (!isValidPassword) {
      store.dispatch(setToastMessage({ msg: "Please enter a valid new password", type: TOAST_ALERT }));
      return;
    }
    if (!isMatched) {
      store.dispatch(setToastMessage({ msg: "Please ensure new passwords are matching", type: TOAST_ALERT }));
      return;
    }

    this.setState({ loading: true });

    const promise = import("../../../authentication/cognito-client").then((module) => {
      return module.CognitoClient.changeUserPassword(this.state.form.currentPassword, this.state.form.newPassword);
    });

    promise
      .then(() => {
        store.dispatch(setToastMessage({ msg: "Password was changed successfully!", type: TOAST_ALERT }));
        this.setState({ loading: false, form: EmptyForm });
      })
      .catch((err) => {
        let errors = this.state.errors;
        let errMsg = "Unexpected error contact Radivision Technical team.";
        const errCode = err?.code;
        switch (errCode) {
          case "NotAuthorizedException":
            errMsg = "Current password is incorrect";
            break;
          case "LimitExceededException":
            errMsg = err.message;
            break;
          case "PasswordNotMatching":
            errMsg = err.message;
            break;
          case "PasswordNotValid":
            errMsg = err.message;
            break;
        }
        errors = {
          hasError: true,
          errors: [errMsg],
        };

        if (err && err._error) {
          errors.errors = [err._error.message];
        }
        this.setState({
          errors,
          loading: false,
        });
      });
  }

  render() {
    return (
      <div className={`settings-section`} style={{ width: "100%" }}>
        <h4>Account Password</h4>
        <div className="row">
          <div className="col-sm-12">
            {this.state.errors.hasError ? (
              <div className="alert alert-danger" role="alert">
                {this.state.errors.errors[0]}
              </div>
            ) : null}
          </div>
        </div>
        <div className="add-media-modal">
          <div className="form-group">
            <label className="has-float-label top">Current Password</label>
            <input
              className="form-control form-control-lg-7"
              id="createAccountPassword"
              type="password"
              value={this.state.form.currentPassword}
              onChange={(event) => {
                this.updateForm("currentPassword", event.target.value);
              }}
              placeholder="Enter your Current Password"
              required={true}
              disabled={this.state.loading}
            />
          </div>
          <div className="form-group">
            <label className="has-float-label top">New Password </label>

            <input
              className={`form-control ${this.state.form.isValidPassword ? "" : "is-invalid"}`}
              type="password"
              value={this.state.form.newPassword}
              onChange={(event) => {
                this.updateForm("newPassword", event.target.value);
              }}
              placeholder="Enter New Password"
              disabled={this.state.loading}
            />
            {this.state.form.isValidPassword ? null : (
              <div className="invalid-feedback">{ERROR_MESSAGES.INVALID_PASSWORD_MESSAGE}</div>
            )}
          </div>
          <div className="form-group">
            <label className="has-float-label top">Re-enter New Password</label>

            <input
              className={`form-control ${this.state.form.isMatched ? "" : "is-invalid"}`}
              type="password"
              placeholder="Re-enter New Password"
              value={this.state.form.matchingPassword}
              onChange={(event) => {
                this.updateForm("matchingPassword", event.target.value);
              }}
              disabled={this.state.loading}
            />
            {this.state.form.isMatched ? null : (
              <div className="invalid-feedback">{ERROR_MESSAGES.INVALID_PASSWORD_REENTER_MESSAGE}</div>
            )}
          </div>
        </div>
        <RdvButton
          form="change-password-form"
          text="Change Password"
          isLoading={this.state.loading}
          disabled={false}
          onClick={this.onChangePassword}
        />
      </div>
    );
  }
}

const mapState = (state: AppState) => ({
  user: state.account.user,
});

export const ChangePassword = connect(mapState)(ChangePasswordComponent);
